import Promise from 'bluebird';
import path from 'path';
import loaderUtils from 'loader-utils';
import _ from 'lodash';
import minimatch from 'minimatch';

/* eslint-disable import/no-commonjs */
const globAsync = Promise.promisify(require('glob'));
const fs = Promise.promisifyAll(require('fs'));
/* eslint-enable */

var isGlobLike = /\*/;
var isTemplateLike = new RegExp('templateRegExp');

function writeFile(globalRef, pattern, file) {
    const {log, compilation, fileDependencies} = globalRef;

    return fs.statAsync(file.absoluteFrom)
    .then((stat) => {
        // We don't write empty directories
        if (stat.isDirectory()) {
            return;
        }

        // If this came from a glob, add it to the file watchlist
        if (pattern.fromType === 'glob') {
            fileDependencies.push(file.absoluteFrom);
        }

        log(`reading ${file.absoluteFrom} to write to assets`);
        return fs.readFileAsync(file.absoluteFrom)
        .then((content) => {
            //var hash = loaderUtils.getHashDigest(content);
            /*
            file.webpackTo = loaderUtils.interpolateName(
                {resourcePath: file.relativeFrom},
                file.webpackTo,
                {content});
            */
            log(`writing ${file.webpackTo} to compilation assets from ${file.absoluteFrom}`);
            compilation.assets[file.webpackTo] = {
                size: function() {
                    return stat.size;
                },
                source: function() {
                    return content;
                }
            };
        });
    });
}

function processPattern(globalRef, pattern) {
    const {log, output} = globalRef;
    const globArgs = _.assign({
        cwd: pattern.context
    }, pattern.fromArgs || {});

    return globAsync(pattern.absoluteFrom, globArgs)
    .each((fileFrom) => {
        const file = {
            absoluteFrom: path.resolve(pattern.context, fileFrom)
        }
        file.relativeFrom = path.relative(pattern.context, file.absoluteFrom);

        // Check the ignore list
        let il = pattern.ignore.length;
        while (il--) {
            const ignoreGlob = pattern.ignore[il];

            let globParams = {
                matchBase: true
            };

            let glob;
            if (_.isString(ignoreGlob)) {
                glob = ignoreGlob;
            } else if (_.isObject(ignoreGlob)) {
                glob = ignoreGlob.glob || '';
                // Overwrite minimatch defaults
                globParams = _.assign(globParams, _.omit(ignoreGlob, ['glob']));
            } else {
                glob = '';
            }

            if (minimatch(file.absoluteFrom, glob, globParams)) {
                log(`ignoring ${file.absoluteFrom}, because it matches the ignore glob ${glob}`);
                return;
            }
        }

        // Change the to path to be relative for webpack
        if (pattern.toType === 'dir') {
            file.webpackTo = path.join(pattern.to, file.relativeFrom);
        } else if (pattern.toType === 'file') {
            file.webpackTo = pattern.to || file.relativeFrom;
        } else if (pattern.toType === 'template') {
            file.webpackToTemplate = pattern.to;
        }

        if (path.isAbsolute(file.webpackTo)) {
            file.webpackTo = path.relative(output, file.webpackTo);
        }

        return writeFile(globalRef, pattern, file);
    });
}

function preProcessPattern(globalRef, pattern) {
    const {log, context, output, fileDependencies, contextDependencies} = globalRef;

    pattern.to = pattern.to || '';
    pattern.context = pattern.context || context;
    pattern.ignore = globalRef.ignore.concat(pattern.ignore || []);

    log(`processing from: '${pattern.from}' to: '${pattern.to}'`);

    switch(true) {
        case !!pattern.toType: // if toType already exists
            break;
        case isTemplateLike.test(pattern.to):
            pattern.toType = 'template';
            break;
        case path.extname(pattern.to) === '':
            pattern.toType = 'dir';
            break;
        default:
            pattern.toType = 'file';
    }

    log(`determined '${pattern.to}' is a '${pattern.toType}'`);

    if (path.isAbsolute(pattern.from)) {
        pattern.absoluteFrom = pattern.from;
    } else {
        pattern.absoluteFrom = path.resolve(pattern.context, pattern.from);
    }

    log(`determined '${pattern.from}' to be read from '${pattern.absoluteFrom}'`);

    return fs
    .statAsync(pattern.absoluteFrom)
    .catch(() => {
        // If from doesn't appear to be a glob, then log a warning
        if (isGlobLike.test(pattern.from)) {
            pattern.fromType = 'glob';
        } else {
            log(`WARNING - unable to locate '${pattern.from}' at '${pattern.absoluteFrom}'`);
        }
    })
    .then((stat) => {
        if (!stat) {
            return pattern;
        }

        if (stat.isDirectory()) {
            pattern.fromType = 'dir';
            pattern.context = pattern.absoluteFrom;
            contextDependencies.push(pattern.absoluteFrom);
            pattern.absoluteFrom = path.join(pattern.absoluteFrom, '**/*');
            pattern.fromArgs = {
                dot: true
            };
            //pattern.context = path.resolve(pattern.context, pattern.relativeFrom);
        } else if(stat.isFile()) {
            pattern.fromType = 'file';
            pattern.context = path.dirname(pattern.absoluteFrom);
            pattern.fromArgs = {
                dot: true
            };
            fileDependencies.push(pattern.absoluteFrom);
        } else if(!pattern.fromType) {
            throw new Error(`[copy-webpack-plugin] Unrecognized file type for ${pattern.from}`);
        }
        return pattern;
    });
}

function CopyWebpackPlugin(patterns = [], options = {}) {
    if (!Array.isArray(patterns)) {
        throw new Error('[copy-webpack-plugin] patterns must be an array');
    }

    function log(msg) {
        if (options.debug) {
            console.log('[copy-webpack-plugin] ' + msg);
        }
    }

    const apply = (compiler) => {
        const fileDependencies = [];
        const contextDependencies = [];

        compiler.plugin('emit', (compilation, cb) => {
            log('starting emit')
            const callback = () => {
                log('finishing emit')
                cb();
            };

            const globalRef = {
                log,
                compilation,
                fileDependencies,
                contextDependencies,
                context: compiler.options.context,
                output: compiler.options.output.path,
                ignore: options.ignore || []
            }

            Promise.each(patterns, (pattern) => {
                // Identify absolute source of each pattern and destination type
                return preProcessPattern(globalRef, pattern)
                .then((pattern) => {
                    // Every source (from) is assumed to exist here
                    return processPattern(globalRef, pattern);
                });
            })
            .catch((err) => {
                compilation.errors.push(err);
            })
            .finally(callback);
        });

        compiler.plugin('after-emit', (compilation, cb) => {
            log('starting after-emit')
            const callback = () => {
                log('finishing after-emit')
                cb();
            }

            // Add file dependencies if they're not already tracked
            _.forEach(fileDependencies, (file) => {
                if (_.includes(compilation.fileDependencies, file)) {
                    log(`not adding ${file} to change tracking, because it's already tracked`);
                } else {
                    log(`adding ${file} to change tracking`);
                    compilation.fileDependencies.push(file);
                }
            });

            // Add context dependencies if they're not already tracked
            _.forEach(contextDependencies, (context) => {
                if (!_.includes(compilation.contextDependencies, context)) {
                    log(`not adding ${context} to change tracking, because it's already tracked`);
                } else {
                    log(`adding ${context} to change tracking`);
                    compilation.contextDependencies.push(context);
                }
            });

            callback();
        });
    };
    
    return {
        apply
    };
}

CopyWebpackPlugin['default'] = CopyWebpackPlugin;
module.exports = CopyWebpackPlugin;
